import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/utils/productionLogger";
import { PageWrapper } from "@/components/layout/PageWrapper";

// Interface pour le retour de la RPC get_invitation_by_token
interface InvitationRpcResponse {
  id: string;
  email: string;
  email_full: string;
  first_name?: string;
  last_name?: string;
  role: string;
  expires_at: string;
  created_at: string;
  organization: {
    id: string;
    name: string;
  };
  error?: boolean;
  message?: string;
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        // Rediriger vers la page d'inscription spéciale pour les invités
        navigate(`/invite/signup?token=${token}`);
        return;
      }
      loadInvitation();
    }
  }, [user, authLoading, token]);

  const loadInvitation = async () => {
    if (!token) {
      setError("Token d'invitation manquant");
      setLoading(false);
      return;
    }

    try {
      // Utiliser la RPC publique pour récupérer l'invitation
      const { data: rpcData, error: rpcError } = await supabase
        .rpc("get_invitation_by_token", { invitation_token: token });

      if (rpcError) {
        logger.error("Error calling get_invitation_by_token RPC", rpcError);
        setError("Erreur lors du chargement de l'invitation");
        setLoading(false);
        return;
      }

      // Cast vers le type approprié
      const invitationData = rpcData as InvitationRpcResponse | null;

      // Vérifier si la RPC a retourné une erreur
      if (invitationData?.error) {
        setError(invitationData.message || "Invitation introuvable ou déjà utilisée");
        setLoading(false);
        return;
      }

      if (!invitationData) {
        setError("Invitation introuvable");
        setLoading(false);
        return;
      }

      // Vérifier si l'email correspond (utiliser email_full après auth)
      if (invitationData.email_full?.toLowerCase() !== user?.email?.toLowerCase()) {
        setError("Cette invitation n'est pas pour votre adresse email");
        setLoading(false);
        return;
      }

      // Vérifier si déjà membre
      const { data: existingMember } = await supabase
        .from("organization_members")
        .select("*")
        .eq("organization_id", invitationData.organization.id)
        .eq("user_id", user?.id)
        .maybeSingle();

      if (existingMember) {
        setError("Vous êtes déjà membre de cette organisation");
        setLoading(false);
        return;
      }

      // Adapter les données au format attendu
      setInvitation({
        id: invitationData.id,
        email: invitationData.email_full,
        role: invitationData.role,
        expires_at: invitationData.expires_at,
        organization_id: invitationData.organization.id
      });
      setOrganization(invitationData.organization);
      setLoading(false);
    } catch (err) {
      logger.error("Error loading invitation", err as Error);
      setError("Erreur lors du chargement de l'invitation");
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token) return;

    setAccepting(true);
    try {
      const { data, error } = await supabase.functions.invoke("accept-organization-invitation", {
        body: { token }
      });

      if (error) {
        toast({
          title: "Erreur",
          description: error.message || "Impossible d'accepter l'invitation",
          variant: "destructive"
        });
      } else {
        setAccepted(true);
        toast({
          title: "Invitation acceptée",
          description: data.message || "Vous avez rejoint l'organisation avec succès"
        });
        
        // Rediriger vers le dashboard après 2 secondes
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      }
    } catch (_error) {
      logger.error("Error accepting invitation", _error as Error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    } finally {
      setAccepting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="flex items-center justify-center p-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 animate-spin" />
                <span>Chargement...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-destructive">Invitation invalide</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/dashboard")} className="w-full">
                Retour au tableau de bord
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  if (accepted) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-green-600">Invitation acceptée !</CardTitle>
              <CardDescription>
                Vous avez rejoint {organization?.name} avec succès. 
                Redirection en cours...
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper layout="no-sidebar">
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Invitation à rejoindre une organisation</CardTitle>
            <CardDescription>
              Vous êtes invité(e) à rejoindre une organisation sur Getproof
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">{organization?.name}</h3>
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-muted-foreground">Rôle proposé:</span>
                <Badge variant={invitation?.role === "admin" ? "default" : "secondary"}>
                  {invitation?.role === "admin" ? "Administrateur" : "Membre"}
                </Badge>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground text-center mb-4">
                En acceptant cette invitation, vous aurez accès aux fonctionnalités 
                de l'organisation selon votre rôle.
              </p>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/dashboard")} 
                  className="flex-1"
                >
                  Refuser
                </Button>
                <Button 
                  onClick={handleAccept} 
                  disabled={accepting}
                  className="flex-1"
                >
                  {accepting ? "Acceptation..." : "Accepter"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}